import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Download, ScanSearch, Users } from 'lucide-react';

const stepIcons = [Download, ScanSearch, Users];

const HowItWorks = () => {
  const { t } = useTranslation();
  const steps = ['step1', 'step2', 'step3'];

  return (
    <section
      id="how-it-works"
      className="relative py-24 bg-white overflow-hidden border-t border-slate-100"
    >
      <div className="container mx-auto px-6 lg:px-12 relative z-10">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl font-display font-bold text-slate-900 mb-6 leading-tight">
            Seamless Workflow
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            From downloading the platform to hiring your first batch of verified workers, the entire process takes minutes.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="grid lg:grid-cols-3 gap-8 md:gap-12 relative">

          {/* Connector line (desktop only) */}
          <div className="hidden lg:block absolute top-[44px] left-[calc(16.666%+3rem)] right-[calc(16.666%+3rem)] h-px bg-slate-200" />

          {steps.map((step, index) => {
            const Icon = stepIcons[index];
            return (
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: index * 0.15, ease: 'easeOut' }}
                className="group relative z-10"
              >
                {/* Step Circle */}
                <div className="flex justify-center mb-8">
                  <div className="relative w-24 h-24 rounded-full bg-white border border-slate-200 flex items-center justify-center shadow-[0_8px_30px_rgba(0,0,0,0.04)] group-hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] group-hover:-translate-y-2 transition-all duration-300">
                    <Icon size={32} className="text-blue-600" strokeWidth={1.5} />

                    {/* Index badge */}
                    <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center shadow-md">
                      <span className="text-xs font-bold text-white">{index + 1}</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="text-center px-4">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {t(`howItWorks.steps.${step}.title`)}
                  </h3>
                  <p className="text-slate-600 leading-relaxed text-[15px]">
                    {t(`howItWorks.steps.${step}.desc`)}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
